<template>
  <div class="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-dark-bg dark:to-dark-bg p-8">
    <div class="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8">
      <!-- Personal Information Card -->
      <div class="bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-md dark:border dark:border-dark-border overflow-hidden flex flex-col h-full">
        <div class="p-6 border-b border-gray-100 dark:border-dark-border flex items-center gap-4">
          <div class="w-10 h-10 bg-blue-600 dark:bg-dark-primary text-white rounded-full flex items-center justify-center">
            <User class="w-5 h-5" />
          </div>
          <div>
            <h2 class="text-xl font-bold text-gray-800 dark:text-dark-text">Personal Information</h2>
            <p class="text-gray-600 dark:text-gray-400 text-sm">Update your basic profile information</p>
          </div>
        </div>

        <form @submit.prevent="updateProfile" class="flex flex-col flex-1">
          <div class="p-6 flex-1">
            <div v-if="profileError" class="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-lg mb-6 text-sm">
              {{ profileError }}
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div class="space-y-2">
                <label for="firstName" class="block font-medium text-gray-700 dark:text-gray-300">First Name</label>
                <input
                    type="text"
                    id="firstName"
                    v-model="profileForm.firstName"
                    required
                    :class="[
                      'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors dark:bg-dark-card dark:text-dark-text', 
                      formErrors.firstName ? 'border-red-300 dark:border-red-800' : 'border-gray-300 dark:border-dark-border'
                    ]"
                    @blur="validateField('firstName')"
                />
                <span v-if="formErrors.firstName" class="text-red-600 dark:text-red-400 text-sm">
                  {{ formErrors.firstName }}
                </span>
              </div>
              <div class="space-y-2">
                <label for="lastName" class="block font-medium text-gray-700 dark:text-gray-300">Last Name</label>
                <input
                    type="text"
                    id="lastName"
                    v-model="profileForm.lastName"
                    required
                    :class="[
                      'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors dark:bg-dark-card dark:text-dark-text', 
                      formErrors.lastName ? 'border-red-300 dark:border-red-800' : 'border-gray-300 dark:border-dark-border'
                    ]"
                    @blur="validateField('lastName')"
                />
                <span v-if="formErrors.lastName" class="text-red-600 dark:text-red-400 text-sm">
                  {{ formErrors.lastName }}
                </span>
              </div>
            </div>

            <div class="space-y-2">
              <label for="email" class="block font-medium text-gray-700 dark:text-gray-300">Email Address</label>
              <input
                  type="email"
                  id="email"
                  v-model="emailValue"
                  required
                  :class="[
                    'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors dark:bg-dark-card dark:text-dark-text', 
                    formErrors.email ? 'border-red-300 dark:border-red-800' : 'border-gray-300 dark:border-dark-border'
                  ]"
                  @blur="validateField('email')"
              />
              <span v-if="formErrors.email" class="text-red-600 dark:text-red-400 text-sm">
                {{ formErrors.email }}
              </span>
            </div>
          </div>

          <div class="p-6 border-t border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-input">
            <button
                type="submit"
                :disabled="updating || hasProfileErrors"
                class="w-full py-2 px-4 bg-blue-600 dark:bg-dark-primary text-white rounded-md font-medium hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors disabled:bg-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save class="w-4 h-4" />
              {{ updating ? 'Saving Changes...' : 'Save Changes' }}
            </button>
          </div>
        </form>
      </div>

      <!-- Security Settings Card -->
      <div class="bg-white dark:bg-dark-card rounded-xl shadow-sm dark:shadow-md dark:border dark:border-dark-border overflow-hidden flex flex-col h-full">
        <div class="p-6 border-b border-gray-100 dark:border-dark-border flex items-center gap-4">
          <div class="w-10 h-10 bg-blue-600 dark:bg-dark-primary text-white rounded-full flex items-center justify-center">
            <Lock class="w-5 h-5" />
          </div>
          <div>
            <h2 class="text-xl font-bold text-gray-800 dark:text-dark-text">Security Settings</h2>
            <p class="text-gray-600 dark:text-gray-400 text-sm">Update your password to keep your account secure</p>
          </div>
        </div>

        <form @submit.prevent="updatePassword" class="flex flex-col flex-1">
          <div class="p-6 flex-1">
            <div v-if="passwordError" class="bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 p-4 rounded-lg mb-6 text-sm">
              {{ passwordError }}
            </div>

            <div class="space-y-6">
              <div class="space-y-2">
                <label for="currentPassword" class="block font-medium text-gray-700 dark:text-gray-300">Current Password</label>
                <div class="relative">
                  <input
                      :type="showPassword.current ? 'text' : 'password'"
                      id="currentPassword"
                      v-model="passwordForm.currentPassword"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors pr-10 dark:bg-dark-card dark:text-dark-text', 
                        formErrors.currentPassword ? 'border-red-300 dark:border-red-800' : 'border-gray-300 dark:border-dark-border'
                      ]"
                      @blur="validateField('currentPassword')"
                  />
                  <button
                      type="button"
                      @click="togglePassword('current')"
                      class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      :aria-label="showPassword.current ? 'Hide password' : 'Show password'"
                  >
                    <component :is="showPassword.current ? EyeOff : Eye" class="w-4 h-4" />
                  </button>
                </div>
                <span v-if="formErrors.currentPassword" class="text-red-600 dark:text-red-400 text-sm">
                  {{ formErrors.currentPassword }}
                </span>
              </div>

              <div class="space-y-2">
                <label for="newPassword" class="block font-medium text-gray-700 dark:text-gray-300">New Password</label>
                <div class="relative">
                  <input
                      :type="showPassword.new ? 'text' : 'password'"
                      id="newPassword"
                      v-model="passwordForm.newPassword"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors pr-10 dark:bg-dark-card dark:text-dark-text', 
                        formErrors.newPassword ? 'border-red-300 dark:border-red-800' : 'border-gray-300 dark:border-dark-border'
                      ]"
                      @input="validatePasswordStrength"
                      @blur="validateField('newPassword')"
                  />
                  <button
                      type="button"
                      @click="togglePassword('new')"
                      class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      :aria-label="showPassword.new ? 'Hide password' : 'Show password'"
                  >
                    <component :is="showPassword.new ? EyeOff : Eye" class="w-4 h-4" />
                  </button>
                </div>
                <span v-if="formErrors.newPassword" class="text-red-600 dark:text-red-400 text-sm">
                  {{ formErrors.newPassword }}
                </span>
              </div>

              <div class="space-y-2">
                <label for="confirmPassword" class="block font-medium text-gray-700 dark:text-gray-300">Confirm New Password</label>
                <div class="relative">
                  <input
                      :type="showPassword.confirm ? 'text' : 'password'"
                      id="confirmPassword"
                      v-model="passwordForm.confirmPassword"
                      required
                      :class="[
                        'w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors pr-10 dark:bg-dark-card dark:text-dark-text', 
                        formErrors.confirmPassword ? 'border-red-300 dark:border-red-800' : 'border-gray-300 dark:border-dark-border'
                      ]"
                      @blur="validateField('confirmPassword')"
                  />
                  <button
                      type="button"
                      @click="togglePassword('confirm')"
                      class="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                      :aria-label="showPassword.confirm ? 'Hide password' : 'Show password'"
                  >
                    <component :is="showPassword.confirm ? EyeOff : Eye" class="w-4 h-4" />
                  </button>
                </div>
                <span v-if="formErrors.confirmPassword" class="text-red-600 dark:text-red-400 text-sm">
                  {{ formErrors.confirmPassword }}
                </span>
              </div>

              <div class="p-4 border border-gray-100 dark:border-dark-border rounded-lg bg-gray-50 dark:bg-dark-input" v-if="passwordForm.newPassword">
                <p class="font-medium text-gray-700 dark:text-gray-300 mb-2">Password must:</p>
                <ul class="space-y-2">
                  <li class="flex items-center gap-2" :class="{ 'text-green-600 dark:text-green-500': hasMinLength, 'text-red-600 dark:text-red-400': !hasMinLength }">
                    <component :is="hasMinLength ? Check : X" class="w-4 h-4" />
                    Be at least 10 characters long
                  </li>
                  <li class="flex items-center gap-2" :class="{ 'text-green-600 dark:text-green-500': hasUpperCase, 'text-red-600 dark:text-red-400': !hasUpperCase }">
                    <component :is="hasUpperCase ? Check : X" class="w-4 h-4" />
                    Contain at least 1 uppercase letter
                  </li>
                  <li class="flex items-center gap-2" :class="{ 'text-green-600 dark:text-green-500': hasLowerCase, 'text-red-600 dark:text-red-400': !hasLowerCase }">
                    <component :is="hasLowerCase ? Check : X" class="w-4 h-4" />
                    Contain at least 1 lowercase letter
                  </li>
                  <li class="flex items-center gap-2" :class="{ 'text-green-600 dark:text-green-500': hasNumber, 'text-red-600 dark:text-red-400': !hasNumber }">
                    <component :is="hasNumber ? Check : X" class="w-4 h-4" />
                    Contain at least 1 number
                  </li>
                  <li class="flex items-center gap-2" :class="{ 'text-green-600 dark:text-green-500': hasSpecial, 'text-red-600 dark:text-red-400': !hasSpecial }">
                    <component :is="hasSpecial ? Check : X" class="w-4 h-4" />
                    Contain at least 1 special character
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="p-6 border-t border-gray-100 dark:border-dark-border bg-gray-50 dark:bg-dark-input">
            <button
                type="submit"
                :disabled="changingPassword || !isValidPassword || hasPasswordErrors"
                class="w-full py-2 px-4 bg-blue-600 dark:bg-dark-primary text-white rounded-md font-medium hover:bg-blue-700 dark:hover:bg-blue-800 transition-colors disabled:bg-gray-400 dark:disabled:bg-gray-700 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <KeyRound class="w-4 h-4" />
              {{ changingPassword ? 'Updating Password...' : 'Update Password' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { User, Lock, Save, KeyRound, Eye, EyeOff, Check, X } from 'lucide-vue-next'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { apiClient } from '../api/client'
import { useToast } from '@/composables/useToast'

const toast = useToast()

const router = useRouter()
const authStore = useAuthStore()

// Form state
const profileForm = ref({
  firstName: '',
  lastName: '',
  email: null as string | null
})

const emailValue = computed({
  get: () => {
    if (!profileForm.value.email) return ''
    return typeof profileForm.value.email === 'string'
        ? profileForm.value.email
        : profileForm.value.email.String || ''
  },
  set: (value: string) => {
    profileForm.value.email = value
  }
})

const passwordForm = ref({
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// UI state
const updating = ref(false)
const changingPassword = ref(false)
const showPassword = ref({
  current: false,
  new: false,
  confirm: false
})

// Error state
const profileError = ref('')
const passwordError = ref('')
const formErrors = ref({
  firstName: '',
  lastName: '',
  email: '',
  currentPassword: '',
  newPassword: '',
  confirmPassword: ''
})

// Password validation
const hasMinLength = computed(() => passwordForm.value.newPassword.length >= 10)
const hasUpperCase = computed(() => /[A-Z]/.test(passwordForm.value.newPassword))
const hasLowerCase = computed(() => /[a-z]/.test(passwordForm.value.newPassword))
const hasNumber = computed(() => /[0-9]/.test(passwordForm.value.newPassword))
const hasSpecial = computed(() => /[!@#$%^&*]/.test(passwordForm.value.newPassword))
const passwordsMatch = computed(() =>
    passwordForm.value.newPassword === passwordForm.value.confirmPassword
)

const isValidPassword = computed(() =>
    hasMinLength.value &&
    hasUpperCase.value &&
    hasLowerCase.value &&
    hasNumber.value &&
    hasSpecial.value &&
    passwordsMatch.value
)

const hasProfileErrors = computed(() =>
    Object.values(formErrors.value).some(error =>
        error && ['firstName', 'lastName', 'email'].includes(error)
    )
)

const hasPasswordErrors = computed(() =>
    Object.values(formErrors.value).some(error =>
        error && ['currentPassword', 'newPassword', 'confirmPassword'].includes(error)
    )
)

// Validation functions
function validateField(field: string) {
  formErrors.value[field] = ''

  switch (field) {
    case 'firstName':
      if (!profileForm.value.firstName.trim()) {
        formErrors.value.firstName = 'First name is required'
      }
      break

    case 'lastName':
      if (!profileForm.value.lastName.trim()) {
        formErrors.value.lastName = 'Last name is required'
      }
      break

    case 'email':
      if (!emailValue.value) {
        formErrors.value.email = 'Email is required'
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue.value)) {
        formErrors.value.email = 'Please enter a valid email address'
      }
      break

    case 'currentPassword':
      if (!passwordForm.value.currentPassword) {
        formErrors.value.currentPassword = 'Current password is required'
      }
      break

    case 'newPassword':
      if (!passwordForm.value.newPassword) {
        formErrors.value.newPassword = 'New password is required'
      } else if (!isValidPassword.value) {
        formErrors.value.newPassword = 'Password does not meet requirements'
      }
      break

    case 'confirmPassword':
      if (!passwordForm.value.confirmPassword) {
        formErrors.value.confirmPassword = 'Please confirm your new password'
      } else if (!passwordsMatch.value) {
        formErrors.value.confirmPassword = 'Passwords do not match'
      }
      break
  }
}

function validatePasswordStrength() {
  validateField('newPassword')
  if (passwordForm.value.confirmPassword) {
    validateField('confirmPassword')
  }
}

function validateForm() {
  validateField('firstName')
  validateField('lastName')
  validateField('email')
  return !hasProfileErrors.value
}

function validatePasswordForm() {
  validateField('currentPassword')
  validateField('newPassword')
  validateField('confirmPassword')
  return !hasPasswordErrors.value
}

const togglePassword = (field: 'current' | 'new' | 'confirm') => {
  showPassword.value[field] = !showPassword.value[field]
}

// API functions
async function fetchProfile() {
  try {
    const response = await apiClient.get('/auth/user-info')
    const user = response.data
    profileForm.value = {
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email
    }
  } catch (err: any) {
    console.error('Error fetching profile:', err)
    showNotification('Failed to load profile information', 'error')
  }
}

async function updateProfile() {
  if (updating.value) return
  if (!validateForm()) return

  try {
    updating.value = true
    profileError.value = ''

    await apiClient.put('/users/' + authStore.userInfo?.id, {
      firstName: profileForm.value.firstName,
      lastName: profileForm.value.lastName,
      email: emailValue.value
    })

    await authStore.refreshUserInfo()
    showNotification('Profile updated successfully', 'success')
  } catch (err: any) {
    console.error('Error updating profile:', err)
    profileError.value = err.response?.data?.error || 'Failed to update profile'
    showNotification('Failed to update profile', 'error')
  } finally {
    updating.value = false
  }
}

async function updatePassword() {
  if (changingPassword.value) return
  if (!validatePasswordForm()) return

  try {
    changingPassword.value = true
    passwordError.value = ''

    await apiClient.post('/auth/change-password', {
      old_password: passwordForm.value.currentPassword,
      new_password: passwordForm.value.newPassword
    })

    // Reset form on success
    passwordForm.value = {
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    }

    showNotification('Password updated successfully', 'success')
  } catch (err: any) {
    console.error('Error updating password:', err)
    const errorMessage = err.response?.data?.error || 'Failed to update password'

    if (errorMessage.includes('Invalid credentials') ||
        errorMessage.includes('incorrect password')) {
      passwordError.value = 'Current password is incorrect'
    } else {
      passwordError.value = errorMessage
    }
  } finally {
    changingPassword.value = false
  }
}

function showNotification(message: string, type: 'success' | 'error') {
  if (type === 'success') {
    toast.success(message)
  } else {
    toast.error(message)
  }
}

onMounted(() => {
  if (!authStore.isAuthenticated) {
    router.push('/login')
    return
  }

  fetchProfile()
})
</script>